import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Person } from '@domain/entities/Person';
import { Banner, Button, Field, Modal, inputClass } from '@presentation/design-system';
import { reasonFor } from '@presentation/format';
import { useAccess } from '@presentation/hooks/useAccess';
import { useSensitiveAccess } from '@presentation/providers/SensitiveAccessProvider';
import { getServices } from '@infrastructure/services';
import styles from './CompensationAction.module.css';

/**
 * El sueldo de una persona, detrás de un engrane.
 *
 * **Era una tarjeta de la rejilla** y ocupaba la columna de en medio de la ficha
 * entera para algo que casi nadie mira: el sueldo lo consultan dos personas.
 * Ahora se abre desde la esquina de la tarjeta de identidad, y la rejilla vuelve a
 * las dos columnas que sí se leen todos los días.
 *
 * **Dos permisos y no uno.** `ver_salario` deja consultarlo; `editar_salario` deja
 * fijarlo. Son trabajos distintos, y quien consulta no tiene por qué poder cambiar
 * la cifra — misma división que `ver_detalle` y `editar_detalle`.
 *
 * **Sin permiso el engrane no existe**, ni apagado. Un control deshabilitado que
 * diga «no tienes acceso al salario» confirma que hay un salario y dónde vive; no
 * dibujarlo no confirma nada.
 *
 * ## Aquí ya no se calcula el finiquito
 *
 * Había un selector de tipo de separación y un desglose completo, y eran la
 * segunda calculadora del portal: **la de verdad vive en «Dar de baja»**, que
 * pregunta los días tomados y si su prima se pagó. Ésta asumía el periodo limpio
 * —cero tomados, prima pagada— y por eso daba otra cifra sobre los mismos datos.
 * Dos números que no cuadran, y el bueno no era el que estaba a mano.
 *
 * Un aumento tampoco es una corrección, y por eso `effectiveFrom` no tiene valor
 * por omisión: el histórico de sueldos es lo que hace auditable un finiquito, y
 * rellenar la fecha de hoy en silencio convertiría cada corrección de un dedazo en
 * un aumento con efecto retroactivo a hoy.
 */

const pesos = (cents: number): string =>
  (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

/** Acepta «30000», «30,000» y «$30,000.50». Devuelve centavos, o null. */
const parsePesos = (raw: string): number | null => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (cleaned.length === 0) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
};

export const CompensationAction = ({ person }: { readonly person: Person }): ReactNode => {
  const { holds } = useAccess();
  /*
   * El permiso dice quién puede; esto comprueba que sigue siendo él.
   *
   * El sueldo de otra persona es el dato más sensible que guarda el portal, y una
   * sesión abierta sólo prueba que alguien se identificó alguna vez. Antes de
   * abrir se vuelve a pasar por Google, y vale diez minutos. Ver
   * `SensitiveAccessProvider`.
   */
  const sensitive = useSensitiveAccess();
  const canView = holds('ver_salario');
  const canEdit = holds('editar_salario');

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /* Se consulta al abrir, no al montar: el sueldo no se enseña en la ficha. */
  const salary = useQuery({
    queryKey: ['salary', person.id],
    enabled: canView && open,
    queryFn: async () => {
      const result = await getServices().salaries.getFor(person.id);
      return result.ok ? result.value : null;
    },
  });

  // El engrane no se dibuja sin permiso. Ver la nota de arriba.
  if (!canView) return null;

  const stored = salary.data;

  const save = async (): Promise<void> => {
    const cents = parsePesos(draft);
    if (cents === null || effectiveFrom.length === 0) return;
    setBusy(true);
    setFailure(null);
    const result = await getServices().salaries.set({
      personId: person.id,
      monthlyCents: cents,
      effectiveFrom,
    });
    setBusy(false);
    if (!result.ok) {
      setFailure(`No se pudo guardar.${reasonFor(result.error)}`);
      return;
    }
    setDraft('');
    setEffectiveFrom('');
    await salary.refetch();
  };

  const close = (): void => {
    setOpen(false);
    setFailure(null);
  };

  return (
    <>
      <button
        type="button"
        className={styles.gear}
        title="Compensación"
        aria-label={`Compensación de ${person.displayName}`}
        onClick={() => {
          void sensitive.ensure().then((autorizado) => {
            /* Si cierra la ventana de Google no se abre nada y no se dice nada
               más: el propio gesto de cancelar ya es la respuesta. */
            if (autorizado) setOpen(true);
          });
        }}
      >
        <span aria-hidden="true">⚙</span>
      </button>

      {open && (
        <Modal
          title={`Compensación de ${person.displayName}`}
          onClose={close}
          footer={
            <Button variant="ghost" onClick={close}>
              Cerrar
            </Button>
          }
        >
          <div className={styles.body}>
            {stored !== null && stored !== undefined ? (
              <div className={styles.salaryRow}>
                <span className={styles.salaryLabel}>Sueldo mensual</span>
                <span className={styles.salaryValue}>{pesos(stored.monthlyCents)}</span>
              </div>
            ) : (
              <Banner tone="info">
                {/* El texto sigue a lo que la persona puede hacer: decirle
                    «regístralo abajo» a quien no tiene `editar_salario` la manda a
                    un campo que no va a encontrar. */}
                {canEdit
                  ? 'Esta persona no tiene sueldo registrado. Regístralo abajo.'
                  : 'Esta persona no tiene sueldo registrado.'}
              </Banner>
            )}

            {canEdit && (
              <div className={styles.editor}>
                <Field
                  label={stored ? 'Corregir el sueldo mensual' : 'Registrar el sueldo mensual'}
                >
                  {(id) => (
                    <input
                      id={id}
                      className={inputClass()}
                      inputMode="decimal"
                      placeholder="30,000.00"
                      value={draft}
                      onChange={(event) => {
                        setDraft(event.target.value);
                        setFailure(null);
                      }}
                    />
                  )}
                </Field>
                <Field
                  label="Vigente desde"
                  hint="Un aumento es un registro nuevo, no una corrección."
                >
                  {(id) => (
                    <input
                      id={id}
                      type="date"
                      className={inputClass()}
                      value={effectiveFrom}
                      onChange={(event) => {
                        setEffectiveFrom(event.target.value);
                      }}
                    />
                  )}
                </Field>
                <Button
                  variant="primary"
                  disabled={busy || parsePesos(draft) === null || effectiveFrom.length === 0}
                  onClick={() => {
                    void save();
                  }}
                >
                  {busy ? 'Guardando…' : 'Guardar sueldo'}
                </Button>
              </div>
            )}

            {failure !== null && <Banner tone="danger">{failure}</Banner>}

            {/* Dónde está lo que ya no está aquí. Sin esta línea, quien venía a
                calcular un finiquito se queda mirando un modal que no lo tiene. */}
            <p className={styles.note}>
              El finiquito se calcula al dar de baja, que es donde se preguntan los
              días tomados y si su prima se pagó.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};
